import { api } from '@/api/client';
import { updateDepositExemption } from '@/api/partners';
import { Button, Card, CardContent, CardHeader, CardTitle } from '@/components/ui';
import { useContactPersons } from '@/hooks/use-contact-persons';
import { usePartnerEditor } from '@/hooks/use-partner-editor';
import { useTabUrlSync } from '@/hooks/use-tab-url-sync';
import { isUuidLike } from '@/lib/utils';
import { useAuthStore } from '@/stores/auth-store';
import { useTranslation } from '@kgc/i18n';
import { AlertTriangle, Ban, ShieldCheck } from 'lucide-react';
import { useCallback, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ContractPriceListTab } from './ContractPriceListTab';
import { AuthorizationGrantTab, type GrantForTab } from './components/AuthorizationGrantTab';
import { PartnerActivityTimeline } from './components/PartnerActivityTimeline';
import { PartnerRoleToggleChips, type PartnerRoleFlag } from './components/PartnerRoleToggleChips';
import { RepresentativesTab } from './components/RepresentativesTab';
import { resolveDepositExemptionErrorKey } from './deposit-exemption-error';
import { PARTNER_CATEGORIES, PARTNER_STATUSES, PARTNER_TYPES } from './mock-data';
import type { PartnerCategory, PartnerStatus } from './types';

// STORY-1212 B-13 scaffold: frozen empty array shared between renders so
// React sees a stable reference (prevents unnecessary memo invalidation in
// the AuthorizationGrantTab). Replaced by a real hook (e.g. useAuthGrants)
// in the follow-up wiring story.
const EMPTY_GRANTS_PLACEHOLDER: GrantForTab[] = [];

// STORY-1212 B-13 role-derivation: the web app's Partner type uses `categories`
// (retail/wholesale/rental/service | supplier | contractor) whereas AC-1 refers
// to the abstract CUSTOMER/SUPPLIER dual-role. Until the Partner.role enum
// schema migration lands (AC-2, deferred follow-up), we derive the role flags
// from existing `categories`.
const CUSTOMER_CATEGORY_SET = new Set(['retail', 'wholesale', 'rental', 'service']);
function derivePartnerRoles(categories: string[] | undefined): PartnerRoleFlag[] {
  if (!categories || categories.length === 0) return [];
  const roles: PartnerRoleFlag[] = [];
  if (categories.some(c => CUSTOMER_CATEGORY_SET.has(c))) roles.push('CUSTOMER');
  if (categories.includes('supplier')) roles.push('SUPPLIER');
  return roles;
}

/** FG-1-3: 180 napos okmány lejárat (inline — kgc-web nem függ @kgc/partners-től) */
function isDocumentExpired(lastDocumentScanDate: Date | null): boolean {
  if (!lastDocumentScanDate) return false;
  const expiryMs = 180 * 24 * 60 * 60 * 1000;
  return Date.now() - new Date(lastDocumentScanDate).getTime() > expiryMs;
}

type TabType =
  | 'overview'
  | 'addresses'
  | 'contacts'
  | 'representatives'
  | 'financial'
  | 'contractPrices'
  | 'authorizationGrants'
  | 'history'
  | 'rentals'
  | 'service'
  | 'invoices'
  | 'loyalty';

export function PartnerDetailPage() {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  // #348 URL-sync tabok (deep-linkelhető + refresh-stabil) — seam-független S2a migráció
  const [activeTab, setActiveTab] = useTabUrlSync('tab', 'overview');
  const [isEditing, setIsEditing] = useState(false);
  // Story 46-8: Suspension state — must be before early returns (Rules of Hooks)
  const [suspensionFeedback, setSuspensionFeedback] = useState<string | null>(null);
  const feedbackTimeoutRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  // QA-220: nem-UUID `:id` (elgepelt URL) eseten NINCS API-hivas — a not-found ag rendereli.
  const idIsUuid = isUuidLike(id);
  const { partner, isLoading, error, refetch } = usePartnerEditor(idIsUuid ? id : undefined);
  // page9 (#420) — a kontakt-lista a dedikált read-seamről (nem a beágyazott
  // partner.contacts-ból): pinnelt kontrakt, isPrimary-first BE-rendezés.
  // QA-220: a partner-lap KET hivast indit; a merés szerint a kontakt-ag is 400-at kapott.
  const { contactPersons } = useContactPersons(idIsUuid ? id : undefined);

  // Story 46-8: Suspension handlers — must be before early returns (Rules of Hooks)
  const showFeedback = useCallback((message: string) => {
    clearTimeout(feedbackTimeoutRef.current);
    setSuspensionFeedback(message);
    feedbackTimeoutRef.current = setTimeout(() => setSuspensionFeedback(null), 3000);
  }, []);

  const handleSuspendPartner = useCallback(async () => {
    if (!partner || !window.confirm(t('partner.suspendConfirm', { name: partner.name }))) return;
    try {
      await api.patch(`/partners-direct/${partner.id}`, { status: 'BLACKLISTED' });
      showFeedback(t('partner.suspended'));
      refetch();
    } catch {
      showFeedback(t('partner.suspendError'));
    }
  }, [partner, showFeedback, refetch]);

  const handleUnsuspendPartner = useCallback(async () => {
    if (!partner || !window.confirm(t('partner.unsuspendConfirm'))) return;
    try {
      await api.patch(`/partners-direct/${partner.id}`, { status: 'ACTIVE' });
      showFeedback(t('partner.unsuspended'));
      refetch();
    } catch {
      showFeedback(t('partner.unsuspendError'));
    }
  }, [partner, showFeedback, refetch]);

  // Q-T031 (Story 78-5): kaució-mentesség wiring — dedikált @Patch(:id/deposit-exemption)
  // endpoint a BOLTVEZETO+ RBAC-guard miatt (a sima partner-PATCH NEM fogadja → pénz-kockázat
  // lenne a vak wiring). A FE role-check csak UX (defenzív union a BE `BOLTVEZETO` ↔ FE
  // `STORE_MANAGER` mappelésre); a BE @Roles a végső autoritatív gate (403 ha nem jogosult).
  const userRole = useAuthStore(s => s.user?.role);
  const canEditDepositExemption =
    userRole != null &&
    ['STORE_MANAGER', 'BOLTVEZETO', 'ADMIN', 'SUPER_ADMIN', 'CENTRAL_ADMIN'].includes(userRole);

  const handleDepositExemptionChange = useCallback(
    async (value: 'REQUIRED' | 'EXEMPT' | 'AUTOMATIC') => {
      // Defense-in-depth: a disabled radio a natív UI-t védi, ez a guard a logikát
      // (programatikus hívás ellen); a BE @Roles a végső autoritatív gate.
      if (!partner || !canEditDepositExemption) return;
      try {
        await updateDepositExemption(partner.id, value);
        showFeedback(t('partner.depositExemptionUpdated'));
        refetch();
      } catch (err) {
        // #853 L1(e): a governance-guard 400 `DEPOSIT_EXEMPTION_LIST_REQUIRED`-et
        // ad, ha kauciómentesre váltanál 0 listázott megbízottal → specifikus,
        // cselekvésre-ösztönző toast (code-first); egyébként a generikus hiba.
        showFeedback(t(resolveDepositExemptionErrorKey(err)));
      }
    },
    [partner, canEditDepositExemption, showFeedback, refetch]
  );

  if (isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <div className="text-center">
          <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-primary border-r-transparent"></div>
          <p className="mt-2 text-gray-500 dark:text-[#ccfbf1]">{t('common.loading')}</p>
        </div>
      </div>
    );
  }

  if (error || !partner) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
            {t('partner.partnerNotFound')}
          </h1>
          {error && <p className="mt-2 text-red-600">{error}</p>}
          <Button onClick={() => navigate('/partners')} className="mt-4">
            {t('partner.backToList')}
          </Button>
        </div>
      </div>
    );
  }

  const formatPrice = (price: number) => {
    return new Intl.NumberFormat('hu-HU', {
      style: 'currency',
      currency: 'HUF',
      maximumFractionDigits: 0,
    }).format(price);
  };

  const formatDate = (dateStr?: string) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('hu-HU');
  };

  const getStatusBadge = (status: PartnerStatus) => {
    const config = PARTNER_STATUSES.find(s => s.value === status);
    return (
      <span className={`rounded-full px-3 py-1 text-sm font-medium ${config?.color ?? ''}`}>
        {config ? t(config.labelKey) : status}
      </span>
    );
  };

  const getCategoryBadges = (categories: PartnerCategory[]) => {
    return categories.map(cat => {
      const config = PARTNER_CATEGORIES.find(c => c.value === cat);
      return (
        <span
          key={cat}
          className={`rounded-full px-2 py-1 text-xs font-medium ${config?.color ?? ''}`}
        >
          {config ? t(config.labelKey) : cat}
        </span>
      );
    });
  };

  const isBlocked = partner.status === 'blocked';

  const tabs: { id: TabType; label: string }[] = [
    { id: 'overview', label: t('partner.tabOverview') },
    { id: 'addresses', label: t('partner.tabAddresses') },
    { id: 'contacts', label: t('partner.tabContacts') },
    { id: 'representatives', label: t('partner.tabRepresentatives') },
    { id: 'financial', label: t('partner.tabFinancial') },
    { id: 'rentals', label: t('partner.tabRentals') },
    { id: 'service', label: t('partner.tabService') },
    { id: 'invoices', label: t('partner.tabInvoices') },
    { id: 'loyalty', label: t('partner.tabLoyalty') },
    { id: 'contractPrices', label: t('partner.contractPrices.title') },
    // STORY-1212 B-13: Authorization Grant tab (ADR-124 D-1+D-2+D-3)
    {
      id: 'authorizationGrants',
      label: t('partner.tabAuthorizationGrants'),
    },
    { id: 'history', label: t('partner.tabHistory') },
  ];

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-[#0f3d47]">
      {/* Header */}
      <header className="bg-white shadow-sm dark:bg-[#1a4f5c]">
        <div className="mx-auto max-w-7xl px-4 py-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Button variant="ghost" onClick={() => navigate('/partners')}>
                {t('common.back')}
              </Button>
              <div>
                <div className="flex flex-wrap items-center gap-3">
                  <h1 className="text-xl font-bold text-gray-900 dark:text-white">
                    {partner.name}
                  </h1>
                  {getStatusBadge(partner.status)}
                  {/* STORY-1212 B-13 (AC-1): dual-role toggle chips.
                      A Partner.role enum backend-migráció (AC-2) deferred follow-up.
                      Amíg nincs bekötve: a chip-ek READ-ONLY-ban jelennek meg
                      (`disabled` + tooltip), NEM dobnak hibát kattintásra (demó-trust:
                      a `throw` JS-hibát adott a pultosnál). A `disabled` miatt az
                      onToggle nem hívódik — defenzív no-op marad. */}
                  <PartnerRoleToggleChips
                    currentRoles={derivePartnerRoles(partner.categories)}
                    disabled
                    disabledReason={t('partner.roleToggle.notYetWired')}
                    onToggle={() => {
                      /* deferred (STORY-1212 AC-2) — disabled, nem hívódik */
                    }}
                  />
                </div>
                <p className="text-sm text-gray-500 dark:text-[#ccfbf1]">
                  {partner.code} •{' '}
                  {partner.type === 'company'
                    ? t('partner.companyLabel')
                    : t('partner.individualLabel')}
                </p>
              </div>
            </div>
            <div className="flex gap-2">
              {isEditing ? (
                <>
                  <Button variant="outline" onClick={() => setIsEditing(false)}>
                    {t('common.cancel')}
                  </Button>
                  <Button
                    className="bg-primary hover:bg-primary/90"
                    onClick={() => setIsEditing(false)}
                  >
                    {t('common.save')}
                  </Button>
                </>
              ) : (
                <>
                  {/* Story 46-8: Suspend/Unsuspend action buttons */}
                  {isBlocked ? (
                    <Button
                      variant="outline"
                      onClick={handleUnsuspendPartner}
                      className="text-green-600 border-green-300 hover:bg-green-50 dark:border-green-800 dark:hover:bg-green-900/20"
                      data-testid="unsuspend-btn"
                    >
                      <ShieldCheck className="h-4 w-4 mr-2" />
                      {t('partner.unsuspend')}
                    </Button>
                  ) : (
                    partner.stats.outstandingBalance > 0 && (
                      <Button
                        variant="outline"
                        onClick={handleSuspendPartner}
                        className="text-red-600 border-red-300 hover:bg-red-50 dark:border-red-800 dark:hover:bg-red-900/20"
                        data-testid="suspend-btn"
                      >
                        <Ban className="h-4 w-4 mr-2" />
                        {t('partner.suspend')}
                      </Button>
                    )
                  )}
                  <Button variant="outline" onClick={() => navigate(`/partners/${id}/edit`)}>
                    {t('common.edit')}
                  </Button>
                  <Button
                    className="bg-primary hover:bg-primary/90"
                    onClick={() => navigate('/rental/new')}
                    disabled={isBlocked}
                    title={isBlocked ? t('partner.blockedRentalTooltip') : undefined}
                  >
                    + {t('partner.newRental')}
                  </Button>
                </>
              )}
            </div>
          </div>

          {/* Tabs */}
          <div className="mt-4 flex gap-1 border-b dark:border-[#2d7d8a]">
            {tabs.map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`px-4 py-2 text-sm font-medium transition-colors ${
                  activeTab === tab.id
                    ? 'border-b-2 border-primary text-primary'
                    : 'text-gray-500 hover:text-gray-700 dark:text-[#99f6e4] dark:hover:text-white'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8">
        {/* Story 46-8: Suspension feedback toast */}
        {suspensionFeedback && (
          <div
            className="fixed top-4 right-4 z-50 p-4 rounded-lg shadow-lg bg-green-100 text-green-800 border border-green-300 flex items-center gap-2 dark:bg-green-900/20 dark:text-green-300 dark:border-green-800"
            role="status"
          >
            <ShieldCheck className="h-5 w-5" />
            {suspensionFeedback}
          </div>
        )}

        {/* Story 46-8: Suspension warning banner */}
        {isBlocked && (
          <div
            className="mb-6 rounded-lg border border-red-300 bg-red-50 p-4 flex items-start gap-3 dark:border-red-800 dark:bg-red-900/20"
            role="alert"
            data-testid="suspension-banner"
          >
            <AlertTriangle className="h-6 w-6 text-red-600 flex-shrink-0 mt-0.5" />
            <div>
              <h3 className="font-semibold text-red-800">{t('partner.suspendedBanner')}</h3>
              <p className="text-sm text-red-700 mt-1">{t('partner.suspendedBannerDesc')}</p>
              {partner.rentalBlocked && (
                <p className="text-sm text-red-600 font-medium mt-2">
                  {t('partner.rentalBlockedNote')}
                </p>
              )}
            </div>
          </div>
        )}

        {/* E-104 Customer 360°: Always-visible KPI strip */}
        <div className="mb-6 grid grid-cols-1 gap-3 sm:grid-cols-3" data-testid="partner-kpi-strip">
          <div className="rounded-lg border bg-white p-4 shadow-sm dark:border-[#2d7d8a] dark:bg-[#1a4f5c]">
            <p className="text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-[#99f6e4]">
              {t('partner.kpi.lifetimeValue')}
            </p>
            <p className="mt-1 text-xl font-bold tabular-nums text-gray-900 dark:text-white">
              {formatPrice(partner.stats.totalRevenue)}
            </p>
          </div>
          <div
            className={`rounded-lg border p-4 shadow-sm ${
              partner.stats.outstandingBalance > 0
                ? 'border-red-300 bg-red-50 dark:border-red-800 dark:bg-red-900/20'
                : 'bg-white dark:border-[#2d7d8a] dark:bg-[#1a4f5c]'
            }`}
          >
            <p className="text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-[#99f6e4]">
              {t('partner.kpi.outstandingBalance')}
            </p>
            <p
              className={`mt-1 text-xl font-bold tabular-nums ${
                partner.stats.outstandingBalance > 0
                  ? 'text-red-700 dark:text-red-300'
                  : 'text-gray-900 dark:text-white'
              }`}
            >
              {formatPrice(partner.stats.outstandingBalance)}
            </p>
          </div>
          <div className="rounded-lg border bg-white p-4 shadow-sm dark:border-[#2d7d8a] dark:bg-[#1a4f5c]">
            <p className="text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-[#99f6e4]">
              {t('partner.kpi.totalRentals')}
            </p>
            <p className="mt-1 text-xl font-bold tabular-nums text-gray-900 dark:text-white">
              {partner.stats.totalRentals}
            </p>
          </div>
        </div>

        {activeTab === 'overview' && (
          <div className="grid gap-6 lg:grid-cols-3">
            {/* Left column - Main info */}
            <div className="lg:col-span-2 space-y-6">
              {/* Basic info */}
              <Card>
                <CardHeader>
                  <CardTitle>{t('partner.basicData')}</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid gap-4 sm:grid-cols-2">
                    <div>
                      <label className="text-sm font-medium text-gray-500 dark:text-[#99f6e4]">
                        {t('partner.name')}
                      </label>
                      <p className="text-gray-900 dark:text-white">{partner.name}</p>
                    </div>
                    {/* QA-108: a jegyzett cég magánszemély-partneren is látszik.
                        A `companyName` a wire-on mindig ott van, de COMPANY-típusnál a BE
                        a `name` másolatát írja bele (partner-direct.controller:597) — ezért
                        az egyenlőség-szűrés: duplikált „Név / Cégnév" sort nem mutatunk.
                        Ratifikált követelmény: Zoli 2026-07-23 („a nevéhez céget kapcsolunk"). */}
                    {partner.companyName && partner.companyName !== partner.name && (
                      <div>
                        <label className="text-sm font-medium text-gray-500 dark:text-[#99f6e4]">
                          {t('partner.companyName')}
                        </label>
                        <p
                          className="text-gray-900 dark:text-white"
                          data-testid="partner-company-name"
                        >
                          {partner.companyName}
                        </p>
                      </div>
                    )}
                    <div>
                      <label className="text-sm font-medium text-gray-500 dark:text-[#99f6e4]">
                        {t('common.type')}
                      </label>
                      <p className="text-gray-900 dark:text-white">
                        {(() => {
                          const pt = PARTNER_TYPES.find(tp => tp.value === partner.type);
                          return pt ? t(pt.labelKey) : partner.type;
                        })()}
                      </p>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-500 dark:text-[#99f6e4]">
                        {t('partner.categories')}
                      </label>
                      <div className="mt-1 flex flex-wrap gap-1">
                        {getCategoryBadges(
                          partner.categories as Parameters<typeof getCategoryBadges>[0]
                        )}
                      </div>
                    </div>
                    {partner.taxNumber && (
                      <div>
                        <label className="text-sm font-medium text-gray-500 dark:text-[#99f6e4]">
                          {t('partner.taxNumber')}
                        </label>
                        <p className="text-gray-900 dark:text-white">{partner.taxNumber}</p>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>

              {/* Personal data - individuals only */}
              {partner.type === 'individual' &&
                (partner.birthDate ||
                  partner.birthPlace ||
                  partner.mothersName ||
                  partner.maskedIdCardNumber) && (
                  <Card>
                    <CardHeader>
                      <div className="flex items-center justify-between">
                        <CardTitle>{t('partner.personalData')}</CardTitle>
                        {isDocumentExpired(
                          partner.lastDocumentScanDate
                            ? new Date(partner.lastDocumentScanDate)
                            : null
                        ) && (
                          <span className="rounded-full bg-destructive px-3 py-1 text-xs font-medium text-destructive-foreground">
                            {t('partner.documentExpiredWarning')}
                          </span>
                        )}
                      </div>
                    </CardHeader>
                    <CardContent>
                      <div className="grid gap-4 sm:grid-cols-2">
                        {partner.birthDate && (
                          <div>
                            <label className="text-sm font-medium text-gray-500 dark:text-[#99f6e4]">
                              {t('partner.birthDate')}
                            </label>
                            <p className="text-gray-900 dark:text-white">{partner.birthDate}</p>
                          </div>
                        )}
                        {partner.birthPlace && (
                          <div>
                            <label className="text-sm font-medium text-gray-500 dark:text-[#99f6e4]">
                              {t('partner.birthPlace')}
                            </label>
                            <p className="text-gray-900 dark:text-white">{partner.birthPlace}</p>
                          </div>
                        )}
                        {partner.mothersName && (
                          <div>
                            <label className="text-sm font-medium text-gray-500 dark:text-[#99f6e4]">
                              {t('partner.mothersName')}
                            </label>
                            <p className="text-gray-900 dark:text-white">{partner.mothersName}</p>
                          </div>
                        )}
                        {partner.maskedIdCardNumber && (
                          <div>
                            <label className="text-sm font-medium text-gray-500 dark:text-[#99f6e4]">
                              {t('partner.idCardNumber')}
                            </label>
                            <p className="text-gray-900 dark:text-white">
                              {partner.maskedIdCardNumber}
                            </p>
                          </div>
                        )}
                        {partner.lastDocumentScanDate && (
                          <div>
                            <label className="text-sm font-medium text-gray-500 dark:text-[#99f6e4]">
                              {t('partner.lastDocumentScanDate')}
                            </label>
                            <p className="text-gray-900 dark:text-white">
                              {new Date(partner.lastDocumentScanDate).toLocaleDateString('hu-HU')}
                            </p>
                          </div>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                )}

              {/* Contact info */}
              <Card>
                <CardHeader>
                  <CardTitle>{t('partner.contactInfo')}</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid gap-4 sm:grid-cols-2">
                    {partner.email && (
                      <div>
                        <label className="text-sm font-medium text-gray-500 dark:text-[#99f6e4]">
                          {t('partner.email')}
                        </label>
                        <p className="text-gray-900 dark:text-white">
                          <a
                            href={`mailto:${partner.email}`}
                            className="text-blue-600 hover:underline"
                          >
                            {partner.email}
                          </a>
                        </p>
                      </div>
                    )}
                    {partner.phone && (
                      <div>
                        <label className="text-sm font-medium text-gray-500 dark:text-[#99f6e4]">
                          {t('partner.phone')}
                        </label>
                        <p className="text-gray-900 dark:text-white">
                          <a
                            href={`tel:${partner.phone}`}
                            className="text-blue-600 hover:underline"
                          >
                            {partner.phone}
                          </a>
                        </p>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>

              {/* FG-1-4: Temporary address display */}
              {partner.temporaryAddress && (
                <Card>
                  <CardHeader>
                    <CardTitle>{t('partner.temporaryAddressSection')}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-foreground">{partner.temporaryAddress}</p>
                    {partner.tempAddressValidUntil && (
                      <p
                        className={`mt-1 text-sm ${
                          new Date(partner.tempAddressValidUntil) < new Date()
                            ? 'text-destructive'
                            : 'text-muted-foreground'
                        }`}
                      >
                        {t('partner.tempAddressValidUntil')}:{' '}
                        {new Date(partner.tempAddressValidUntil).toLocaleDateString('hu-HU')}
                        {new Date(partner.tempAddressValidUntil) < new Date() && (
                          <span className="ml-1">{t('partner.temporaryAddressExpired')}</span>
                        )}
                      </p>
                    )}
                  </CardContent>
                </Card>
              )}

              {/* Notes */}
              {partner.notes && (
                <Card>
                  <CardHeader>
                    <CardTitle>{t('common.notes')}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    {partner.notes && (
                      <div className="mb-4">
                        <label className="text-sm font-medium text-gray-500 dark:text-[#99f6e4]">
                          {t('partner.publicNote')}
                        </label>
                        <p className="mt-1 text-foreground">{partner.notes}</p>
                      </div>
                    )}
                  </CardContent>
                </Card>
              )}
            </div>

            {/* Right column - Stats & Quick actions */}
            <div className="space-y-6">
              {/* Stats */}
              <Card>
                <CardHeader>
                  <CardTitle>{t('partner.statistics')}</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    <div className="flex justify-between">
                      <span className="text-gray-500 dark:text-[#ccfbf1]">
                        {t('partner.totalRevenue')}:
                      </span>
                      <span className="font-bold text-gray-900 dark:text-white">
                        {formatPrice(partner.stats.totalRevenue)}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-500 dark:text-[#ccfbf1]">
                        {t('partner.ordersCount')}:
                      </span>
                      <span className="font-medium">{partner.stats.totalOrders} db</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-500 dark:text-[#ccfbf1]">
                        {t('partner.rentalsCount')}:
                      </span>
                      <span className="font-medium">{partner.stats.totalRentals} db</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-500 dark:text-[#ccfbf1]">
                        {t('partner.serviceOrdersCount')}:
                      </span>
                      <span className="font-medium">{partner.stats.totalServiceOrders} db</span>
                    </div>
                    <div className="border-t pt-4 dark:border-[#2d7d8a]">
                      <div className="flex justify-between">
                        <span className="text-gray-500 dark:text-[#ccfbf1]">
                          {t('partner.lastOrder')}:
                        </span>
                        <span className="font-medium">
                          {formatDate(partner.stats.lastOrderDate ?? undefined)}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-500 dark:text-[#ccfbf1]">
                          {t('partner.lastRental')}:
                        </span>
                        <span className="font-medium">
                          {formatDate(partner.stats.lastRentalDate ?? undefined)}
                        </span>
                      </div>
                    </div>
                    {partner.stats.outstandingBalance > 0 && (
                      <div className="border-t pt-4 dark:border-[#2d7d8a]">
                        <div className="flex justify-between">
                          <span className="font-medium text-red-600">{t('partner.debt')}:</span>
                          <span className="font-bold text-red-600">
                            {formatPrice(partner.stats.outstandingBalance)}
                          </span>
                        </div>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>

              {/* Quick actions */}
              <Card>
                <CardHeader>
                  <CardTitle>{t('admin.quickActions')}</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    <Button
                      variant="outline"
                      className="w-full justify-start"
                      onClick={() => navigate('/rental/new')}
                    >
                      {t('partner.newRental')}
                    </Button>
                    <Button
                      variant="outline"
                      className="w-full justify-start"
                      onClick={() => navigate('/sales/new')}
                    >
                      {t('partner.newSale')}
                    </Button>
                    <Button
                      variant="outline"
                      className="w-full justify-start"
                      onClick={() => navigate('/worksheet/new')}
                    >
                      {t('partner.newWorksheet')}
                    </Button>
                  </div>
                </CardContent>
              </Card>

              {/* Rental settings */}
              {partner.categories.includes('rental') && (
                <Card>
                  <CardHeader>
                    <CardTitle>{t('partner.rentalSettings')}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      <div className="flex justify-between">
                        <span className="text-gray-500 dark:text-[#ccfbf1]">
                          {t('partner.rentalStatus')}:
                        </span>
                        {partner.rentalBlocked ? (
                          <span className="font-medium text-red-600">{t('partner.blocked')}</span>
                        ) : (
                          <span className="font-medium text-green-600">{t('partner.allowed')}</span>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* FG-2-3: Költségviselő fa-nézet */}
              {(partner.costBearer ||
                (partner.costBearerClients && partner.costBearerClients.length > 0)) && (
                <Card>
                  <CardHeader>
                    <CardTitle>{t('partner.costBearerTree')}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-2">
                      {/* Ha ez a partner egy magánszemély és van költségviselő cége */}
                      {partner.costBearer && (
                        <div className="text-sm">
                          <span className="text-muted-foreground">
                            {t('partner.costBearerCompany')}:
                          </span>
                          <button
                            onClick={() => navigate(`/partners/${partner.costBearer!.id}`)}
                            className="ml-2 font-medium text-primary hover:underline"
                          >
                            {partner.costBearer.name}
                          </button>
                        </div>
                      )}
                      {/* Ha ez a partner egy cég és vannak alkalmazottai */}
                      {partner.costBearerClients && partner.costBearerClients.length > 0 && (
                        <div>
                          <p className="mb-2 text-sm text-muted-foreground">
                            {t('partner.costBearerEmployees')} ({partner.costBearerClients.length})
                          </p>
                          <ul className="space-y-1 border-l-2 border-muted pl-4">
                            {partner.costBearerClients.map(
                              (client: { id: string; name: string }) => (
                                <li key={client.id}>
                                  <button
                                    onClick={() => navigate(`/partners/${client.id}`)}
                                    className="text-sm font-medium text-primary hover:underline"
                                  >
                                    {client.name}
                                  </button>
                                </li>
                              )
                            )}
                          </ul>
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>
              )}
            </div>
          </div>
        )}

        {activeTab === 'addresses' && (
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle>{t('partner.tabAddresses')}</CardTitle>
              <Button variant="outline" size="sm">
                + {t('partner.newAddress')}
              </Button>
            </CardHeader>
            <CardContent>
              {partner.addresses.length === 0 ? (
                <p className="text-gray-500 dark:text-[#ccfbf1]">{t('partner.noAddresses')}</p>
              ) : (
                <div className="grid gap-4 sm:grid-cols-2">
                  {partner.addresses.map(addr => (
                    <div key={addr.id} className="rounded-lg border p-4 dark:border-[#2d7d8a]">
                      <div className="mb-2 flex items-center gap-2">
                        <span className="font-medium">
                          {addr.type === 'billing'
                            ? t('partner.addressBilling')
                            : addr.type === 'shipping'
                              ? t('partner.addressShipping')
                              : t('partner.addressOther')}
                        </span>
                        {addr.isDefault && (
                          <span className="rounded bg-blue-100 px-2 py-0.5 text-xs text-blue-800 dark:bg-blue-900/20 dark:text-blue-300">
                            {t('partner.defaultAddress')}
                          </span>
                        )}
                      </div>
                      <p className="text-gray-900 dark:text-white">
                        {addr.postalCode} {addr.city}
                      </p>
                      <p className="text-gray-900 dark:text-white">{addr.street}</p>
                      <p className="text-gray-500 dark:text-[#ccfbf1]">{addr.country}</p>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {activeTab === 'contacts' && (
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle>{t('partner.tabContacts')}</CardTitle>
              <Button variant="outline" size="sm">
                + {t('partner.newContact')}
              </Button>
            </CardHeader>
            <CardContent>
              {/* page9 (#420): a lista a ContactPerson read-seamről jön (isPrimary-first BE-rendezés) */}
              {contactPersons.length === 0 ? (
                <p className="text-gray-500 dark:text-[#ccfbf1]">{t('partner.noContacts')}</p>
              ) : (
                <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                  {contactPersons.map(contact => (
                    <div
                      key={contact.id}
                      data-testid="contact-person-card"
                      className="rounded-lg border p-4 dark:border-[#2d7d8a]"
                    >
                      <div className="mb-2 flex items-center gap-2">
                        <span className="font-medium">{contact.name}</span>
                        {contact.isPrimary && (
                          <span
                            data-testid="contact-primary-badge"
                            className="rounded bg-green-100 px-2 py-0.5 text-xs text-green-800 dark:bg-green-900/20 dark:text-green-300"
                          >
                            {t('partner.primary')}
                          </span>
                        )}
                      </div>
                      {contact.position && (
                        <p className="text-sm text-gray-500 dark:text-[#ccfbf1]">
                          {contact.position}
                        </p>
                      )}
                      {contact.email && (
                        <p className="mt-2">
                          <a
                            href={`mailto:${contact.email}`}
                            className="text-sm text-blue-600 hover:underline"
                          >
                            {contact.email}
                          </a>
                        </p>
                      )}
                      {contact.phone && (
                        <p>
                          <a
                            href={`tel:${contact.phone}`}
                            className="text-sm text-blue-600 hover:underline"
                          >
                            {contact.phone}
                          </a>
                        </p>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {activeTab === 'representatives' && <RepresentativesTab partnerId={partner.id} />}

        {activeTab === 'financial' && (
          <div className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>{t('partner.financialSettings')}</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
                  <div>
                    <label className="text-sm font-medium text-gray-500 dark:text-[#99f6e4]">
                      {t('partner.paymentTerm')}
                    </label>
                    <p className="text-lg font-medium text-gray-900 dark:text-white">
                      {t('partner.nDays', { count: partner.paymentTermDays })}
                    </p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-500 dark:text-[#99f6e4]">
                      {t('partner.creditLimit')}
                    </label>
                    <p className="text-lg font-medium text-gray-900 dark:text-white">
                      {partner.creditLimit
                        ? formatPrice(partner.creditLimit)
                        : t('partner.noneValue')}
                    </p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-500 dark:text-[#99f6e4]">
                      {t('partner.baseDiscount')}
                    </label>
                    <p className="text-lg font-medium text-gray-900 dark:text-white">
                      {partner.defaultDiscountPc ?? 0}%
                    </p>
                  </div>
                </div>

                {/* Kedvezmény kategorizálás (O-PRT-1) */}
                {(partner.discountRental != null ||
                  partner.discountSales != null ||
                  partner.discountService != null) && (
                  <div className="mt-4 border-t pt-4 dark:border-[#2d7d8a]">
                    <h4 className="mb-3 text-sm font-semibold text-gray-700 dark:text-[#ccfbf1]">
                      {t('partner.discount.categorized')}
                    </h4>
                    <p className="mb-3 text-xs text-gray-500 dark:text-[#99f6e4]">
                      {t('partner.discount.noStacking')}
                    </p>
                    <div className="grid gap-4 sm:grid-cols-3">
                      <div>
                        <label className="text-sm font-medium text-gray-500 dark:text-[#99f6e4]">
                          {t('partner.discount.rental')}
                        </label>
                        <p className="text-lg font-medium text-gray-900 dark:text-white">
                          {partner.discountRental ?? 0}%
                        </p>
                      </div>
                      <div>
                        <label className="text-sm font-medium text-gray-500 dark:text-[#99f6e4]">
                          {t('partner.discount.sales')}
                        </label>
                        <p className="text-lg font-medium text-gray-900 dark:text-white">
                          {partner.discountSales ?? 0}%
                        </p>
                      </div>
                      <div>
                        <label className="text-sm font-medium text-gray-500 dark:text-[#99f6e4]">
                          {t('partner.discount.service')}
                        </label>
                        <p className="text-lg font-medium text-gray-900 dark:text-white">
                          {partner.discountService ?? 0}%
                        </p>
                      </div>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>{t('partner.debtsTitle')}</CardTitle>
              </CardHeader>
              <CardContent>
                {partner.stats.outstandingBalance > 0 ? (
                  <div className="rounded-lg border border-red-200 bg-red-50 p-4 dark:border-red-800 dark:bg-red-900/20">
                    <p className="text-lg font-bold text-red-600">
                      {t('partner.outstandingDebt')}:{' '}
                      {formatPrice(partner.stats.outstandingBalance)}
                    </p>
                    <p className="text-sm text-red-500 dark:text-red-400">
                      {t('partner.contactSupport')}
                    </p>
                  </div>
                ) : (
                  <p className="text-green-600 dark:text-green-400">
                    {t('partner.noOutstandingDebt')}
                  </p>
                )}
              </CardContent>
            </Card>
          </div>
        )}

        {/* FG-2-1: Tranzakció tab-ok */}
        {activeTab === 'rentals' && (
          <Card>
            <CardHeader>
              <CardTitle>{t('partner.tabRentals')}</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="py-8 text-center text-muted-foreground">
                {t('partner.transactionTabPlaceholder')}
              </p>
            </CardContent>
          </Card>
        )}

        {activeTab === 'service' && (
          <Card>
            <CardHeader>
              <CardTitle>{t('partner.tabService')}</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="py-8 text-center text-muted-foreground">
                {t('partner.transactionTabPlaceholder')}
              </p>
            </CardContent>
          </Card>
        )}

        {activeTab === 'invoices' && (
          <Card>
            <CardHeader>
              <CardTitle>{t('partner.tabInvoices')}</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="py-8 text-center text-muted-foreground">
                {t('partner.transactionTabPlaceholder')}
              </p>
            </CardContent>
          </Card>
        )}

        {activeTab === 'loyalty' && (
          <Card>
            <CardHeader>
              <CardTitle>{t('partner.tabLoyalty')}</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">{t('partner.loyaltyPoints')}:</span>
                  <span className="text-2xl font-bold">{partner.loyaltyPoints ?? 0}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">{t('partner.loyaltyTier')}:</span>
                  <span className="font-medium">{partner.loyaltyTier?.name ?? '-'}</span>
                </div>

                {/* Story 78-5: Kaució mentesség toggle */}
                <div className="border-t pt-4 dark:border-[#2d7d8a]">
                  <h4 className="mb-3 text-sm font-semibold text-foreground">
                    {t('partner.depositExemption')}
                  </h4>
                  {!canEditDepositExemption && (
                    <p className="mb-2 text-xs text-muted-foreground">
                      {t('partner.depositExemptionRbacHint')}
                    </p>
                  )}
                  <div className="space-y-2">
                    {(['REQUIRED', 'EXEMPT', 'AUTOMATIC'] as const).map(option => (
                      <label
                        key={option}
                        className="flex cursor-pointer items-center gap-3 rounded-lg border p-3 transition-all hover:bg-muted dark:border-[#2d7d8a]"
                      >
                        <input
                          type="radio"
                          name="depositExemption"
                          value={option}
                          checked={(partner.depositExemption ?? 'REQUIRED') === option}
                          disabled={!canEditDepositExemption}
                          onChange={() => handleDepositExemptionChange(option)}
                          className="text-primary focus:ring-primary disabled:cursor-not-allowed disabled:opacity-60"
                        />
                        <div>
                          <p className="text-sm font-medium text-foreground">
                            {option === 'REQUIRED' && t('partner.depositRequired')}
                            {option === 'EXEMPT' && t('partner.depositExempt')}
                            {option === 'AUTOMATIC' && t('partner.depositAutomatic')}
                          </p>
                          <p className="text-xs text-muted-foreground">
                            {option === 'REQUIRED' && t('partner.depositRequiredDesc')}
                            {option === 'EXEMPT' && t('partner.depositExemptDesc')}
                            {option === 'AUTOMATIC' && t('partner.depositAutomaticDesc')}
                          </p>
                        </div>
                      </label>
                    ))}
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {activeTab === 'contractPrices' && <ContractPriceListTab partnerId={partner.id} />}

        {activeTab === 'authorizationGrants' && (
          // STORY-1212 B-13 — scaffold: grants list fetching + revoke + create
          // handlers wired in a follow-up story (requires a /api/v1/auth-grants
          // endpoint + hook). Rendering with empty arrays until then so the
          // tab surface is live + design-reviewable.
          <AuthorizationGrantTab
            partnerId={partner.id}
            grantsGiven={EMPTY_GRANTS_PLACEHOLDER}
            grantsReceived={EMPTY_GRANTS_PLACEHOLDER}
          />
        )}

        {activeTab === 'history' && (
          <Card>
            <CardContent className="pt-6">
              <PartnerActivityTimeline partnerId={partner.id} />
            </CardContent>
          </Card>
        )}
      </main>
    </div>
  );
}
